/**
 * Merchant normalization and similarity (deterministic, no AI).
 *
 * "CONAD S.P.A.", "Conad Torino" and "CONAD" all normalize to the key "CONAD". Variants that keep
 * a distinguishing word ("CONAD CITY") get their own key but still score as similar, so the
 * decision to treat them as the same merchant stays with the user.
 */

/** Company-form tokens removed from merchant names (after punctuation is stripped). */
const LEGAL_FORMS = new Set(["SPA", "SRL", "SRLS", "SNC", "SAS", "SS", "SCARL", "SCRL", "COOP", "SOC", "COOPERATIVA", "SOCIETA", "GROUP", "ITALIA", "ITALY", "LTD", "GMBH", "INC"]);

/** Words that introduce an address: everything from here on is dropped (unless it is the first word). */
const ADDRESS_WORDS = new Set(["VIA", "VIALE", "CORSO", "CSO", "PIAZZA", "PZA", "PZZA", "LARGO", "STRADA", "VICOLO", "LUNGOTEVERE", "LOC", "LOCALITA", "FRAZ"]);

/** Store identifiers that are noise for matching ("PV 456", "FILIALE 12", "N 3"). */
const STORE_WORDS = new Set(["PV", "PUNTO", "VENDITA", "FILIALE", "NEGOZIO", "STORE", "SHOP", "N", "NR", "NUM", "CODICE", "COD"]);

/** Italian cities (Piedmont first: the user lives in Turin). Extend as needed; the list only removes noise. */
const CITIES = new Set([
  "TORINO", "MONCALIERI", "RIVOLI", "COLLEGNO", "GRUGLIASCO", "NICHELINO", "CHIERI", "SETTIMO", "VENARIA", "ORBASSANO", "PINEROLO", "IVREA",
  "ASTI", "CUNEO", "ALESSANDRIA", "NOVARA", "VERCELLI", "BIELLA", "AOSTA",
  "MILANO", "MONZA", "BERGAMO", "BRESCIA", "COMO", "LECCO", "VARESE", "PAVIA", "CREMONA", "MANTOVA", "LODI", "SONDRIO",
  "GENOVA", "SAVONA", "IMPERIA", "SPEZIA", "VENEZIA", "VERONA", "PADOVA", "VICENZA", "TREVISO", "ROVIGO", "BELLUNO",
  "TRENTO", "BOLZANO", "TRIESTE", "UDINE", "GORIZIA", "PORDENONE",
  "BOLOGNA", "MODENA", "PARMA", "REGGIO", "PIACENZA", "FERRARA", "RAVENNA", "RIMINI", "FORLI", "CESENA",
  "FIRENZE", "PISA", "LIVORNO", "LUCCA", "SIENA", "AREZZO", "PRATO", "PISTOIA", "GROSSETO",
  "ROMA", "NAPOLI", "SALERNO", "BARI", "LECCE", "TARANTO", "FOGGIA", "PALERMO", "CATANIA", "MESSINA", "CAGLIARI", "SASSARI",
  "PERUGIA", "ANCONA", "PESCARA", "CHIETI", "AQUILA", "CAMPOBASSO", "POTENZA", "CATANZARO", "COSENZA",
]);

/** Uppercase, accents removed, punctuation → spaces, whitespace collapsed. */
function basicClean(raw: string): string {
  return raw
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/&/g, " ")
    // "S.P.A." / "S.R.L." → "SPA" / "SRL" before punctuation becomes spaces
    .replace(/\b([A-Z])\.(?=[A-Z]\.)/g, "$1")
    .replace(/\b([A-Z])\.([A-Z])\.?/g, "$1$2")
    .replace(/[^A-Z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Matching key for a merchant name, or null when nothing meaningful is left.
 *  - accents, case and punctuation are ignored
 *  - legal forms (SPA, SRL…), store codes, numbers and VAT numbers are removed
 *  - an address ("VIA ROMA 12") and a trailing city ("TORINO") are removed
 */
export function normalizeMerchant(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const text = basicClean(raw.replace(/P\.?\s*IVA\s*[:.]?\s*\d+/gi, " "));
  const tokens: string[] = [];
  for (const token of text.split(" ")) {
    if (!token) continue;
    if (tokens.length > 0 && ADDRESS_WORDS.has(token)) break;
    if (/\d/.test(token)) continue;
    if (LEGAL_FORMS.has(token) || STORE_WORDS.has(token)) continue;
    tokens.push(token);
  }
  // Drop city names, but never the whole name ("ROMA" alone stays a merchant called Roma).
  const withoutCities = tokens.filter((t) => !CITIES.has(t));
  const kept = withoutCities.length > 0 ? withoutCities : tokens;
  return kept.length > 0 ? kept.join(" ") : null;
}

function bigrams(text: string): Map<string, number> {
  const compact = text.replace(/ /g, "");
  const out = new Map<string, number>();
  for (let i = 0; i < compact.length - 1; i++) {
    const g = compact.slice(i, i + 2);
    out.set(g, (out.get(g) ?? 0) + 1);
  }
  return out;
}

/** Sørensen–Dice coefficient on character bigrams (tolerates small typos: "ESSELUGA"). */
function dice(a: string, b: string): number {
  const x = bigrams(a);
  const y = bigrams(b);
  let overlap = 0;
  let total = 0;
  for (const n of x.values()) total += n;
  for (const n of y.values()) total += n;
  for (const [g, n] of x) overlap += Math.min(n, y.get(g) ?? 0);
  return total === 0 ? 0 : (2 * overlap) / total;
}

/**
 * Similarity of two merchant names in [0, 1], or null when either is unknown.
 *  1.0 same normalized key ("CONAD S.P.A." ≈ "Conad Torino")
 *  0.8 one name is contained in the other ("CONAD" ⊂ "CONAD CITY")
 *  otherwise the bigram similarity of the keys, slightly discounted
 */
export function merchantSimilarity(a: string | null | undefined, b: string | null | undefined): number | null {
  const ka = normalizeMerchant(a);
  const kb = normalizeMerchant(b);
  if (!ka || !kb) return null;
  if (ka === kb) return 1;
  const ta = new Set(ka.split(" "));
  const tb = new Set(kb.split(" "));
  const [small, large] = ta.size <= tb.size ? [ta, tb] : [tb, ta];
  if ([...small].every((t) => large.has(t)) && [...small].some((t) => t.length >= 3)) return 0.8;
  return Math.round(dice(ka, kb) * 0.9 * 1000) / 1000;
}
